import type { Campaign } from './data';
import type { buildDetails } from './details';
import { type Filters, type Model } from './model';
import { metadataNumber, opportunityValues } from './selectionModel';

type Cell = string | number | null | undefined;
/** Complete settled view operands; CSV escaping and the download stay at the browser boundary. */
export function reportRows(
  model: Model,
  details: ReturnType<typeof buildDetails>,
  filters: Filters,
  tab: number,
  campaigns: Array<Campaign>,
  selectedCurrency?: string,
  currentOnly = tab === 2,
): Array<Array<Cell>> {
  const sides = currentOnly ? (['current'] as const) : (['current', 'comparison'] as const);
  const rows: Array<Array<Cell>> = [
    ['Report', 'Post Campaign Report (LPC)'],
    ['View', ['Deal selection', 'Vendor report', 'Vendor current only', 'PCM report', 'PCM booking mix'][tab]],
    ['Current', filters.current, filters.from, filters.to],
    ...(!currentOnly ? [['Comparison', filters.comparison, filters.cmpFrom, filters.cmpTo]] : []),
    ...Object.entries(filters)
      .filter(([key]) => !currentOnly || !['comparison', 'cmpFrom', 'cmpTo'].includes(key))
      .map(([key, value]) => ['Filter', key, value]),
  ];
  if (tab === 0) {
    return [
      ...rows,
      [
        'Opportunity',
        'Country',
        'Area Level 1',
        'Observed start',
        'Observed end',
        'Room nights',
        'Vendor revenue after holdback',
        'Currency',
        'L12M TTV AUD',
        'Source rank',
      ],
      ...campaigns.map((c) => {
        const v = opportunityValues(c, filters.currency, filters.refund, selectedCurrency);
        return [
          c.offer_opportunity_name,
          c.offer_country_name,
          c.offer_geo_area_level_1,
          c.start_date,
          c.end_date,
          v.nights,
          v.revenue,
          v.currency,
          metadataNumber(c.l12m_ttv_aud),
          c.source_top50_rank,
        ];
      }),
    ];
  }
  rows.push(['Measure', ...sides]);
  Object.keys(model.current.totals).forEach((key) => rows.push([key, ...sides.map((side) => model[side].totals[key])]));
  rows.push(['Average price difference', ...sides.map((side) => model.parity[side].priceDiff)]);
  rows.push([
    'Period day',
    'Current cumulative room nights',
    ...(!currentOnly ? ['Comparison cumulative room nights'] : []),
    'Current cumulative views',
    ...(!currentOnly ? ['Comparison cumulative views'] : []),
  ]);
  model.curve.forEach((p) =>
    rows.push([
      p.day,
      p.current,
      ...(!currentOnly ? [p.comparison] : []),
      p.views,
      ...(!currentOnly ? [p.cmpViews] : []),
    ]),
  );
  model.roomCurves.forEach((room) => {
    rows.push(['Room nights curve', room.name, 'Period day', 'Cumulative room nights']);
    room.values.forEach((p) => rows.push(['Room nights curve', room.name, p.day, p.nights]));
  });
  const groups: Array<readonly [string, ReturnType<typeof buildDetails>['guestMix']]> = [
    ['Room type', details.roomShares],
    ['Check-in weekday', model.weekdays],
    ['Lead time', model.leads],
    ['Guest group', details.guestMix],
    ['Room and guest group', details.roomGuestMix],
    ['Customer details', details.customerDetails],
    ['Package type', details.packageMix],
  ];
  for (const [group, values] of groups) {
    rows.push([
      group,
      'Period',
      'Packages',
      'Bookings',
      'Room nights',
      'Revenue',
      'ADR',
      'ALOS',
      'Lead time',
      'Package share',
    ]);
    const totals = Object.fromEntries(
      sides.map((side) => [side, values.reduce((n, row) => n + (row[side].packages || 0), 0)]),
    );
    values.forEach((row) =>
      sides.forEach((side) => {
        const v = row[side];
        const total = totals[side];
        rows.push([
          row.name,
          side,
          v.packages,
          v.orders,
          v.nights,
          v.revenue,
          v.adr,
          v.alos,
          v.lead,
          total && v.packages !== null ? v.packages / total : null,
        ]);
      }),
    );
  }
  rows.push(['Check-in month', 'Period', 'Calendar month', 'Bookings', 'Bookings after refunds', 'Vendor revenue']);
  details.monthRows.forEach((row) =>
    sides.forEach((side) =>
      rows.push([
        row.name,
        side,
        row[side]?.calendarMonth,
        row[side]?.totals.orders,
        row[side]?.afterRefund.orders,
        row[side]?.totals.revenue,
      ]),
    ),
  );
  if (currentOnly) {
    rows.push(['Check-in class', 'Current transaction period bookings after refunds']);
    details.checkinClassRows.filter((row) => row.current !== null).forEach((row) => rows.push([row.name, row.current]));
  } else {
    rows.push([
      'Check-in class',
      'Bookings after refunds',
      'Current transaction period bookings after refunds',
      'Comparison transaction period bookings after refunds',
    ]);
    details.checkinClassRows.forEach((row) => rows.push([row.name, row.value, row.current, row.comparison]));
  }
  rows.push(['Regional share day', 'Current booking share', ...(!currentOnly ? ['Comparison booking share'] : [])]);
  model.regionalCurve.forEach((p) => rows.push([p.day, p.current, ...(!currentOnly ? [p.comparison] : [])]));
  rows.push(['Regional view share day', 'Current view share', ...(!currentOnly ? ['Comparison view share'] : [])]);
  model.regionalViewCurve.forEach((p) => rows.push([p.day, p.current, ...(!currentOnly ? [p.comparison] : [])]));
  rows.push(['Region measure', ...sides]);
  for (const metric of ['bookings', 'views'] as const) {
    rows.push([metric, ...sides.map((side) => details.regionSummary[side][metric])]);
  }
  rows.push(['Period day', 'Bookings', 'Average price difference', 'Conversion rate']);
  details.parityCvr.forEach((p) => rows.push([p.day, p.bookings, p.priceDiff, p.cvr]));
  details.channelCurves.forEach((channel) => {
    rows.push(['Channel curve', channel.name, 'Period day', ...sides]);
    channel.points.forEach((p) => rows.push(['Channel curve', channel.name, p.day, ...sides.map((side) => p[side])]));
  });
  return rows;
}
